import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useBreadcrumbs } from '../context/BreadcrumbsContext';

const Breadcrumbs = () => {
  const location = useLocation();
  const { customBreadcrumbs } = useBreadcrumbs();
  const pathnames = location.pathname.split('/').filter((x) => x);

  const breadcrumbNames = {
    catalog: 'Каталог',
    calculator: 'Калькулятор',
    portfolio: 'Портфолио',
    about: 'О компании',
    contacts: 'Контакты',
    order: 'Заказать форму',
    cart: 'Корзина',
    checkout: 'Оформление заказа',
    blog: 'Блог',
    product: 'Товар',
    legal: 'Юридическая информация',
    privacy: 'Политика конфиденциальности',
    terms: 'Условия использования',
    cookies: 'Политика cookies',
    requisites: 'Реквизиты'
  };

  // Don't show breadcrumbs on homepage
  if (pathnames.length === 0) return null;

  const baseUrl = 'https://avk-pro.ru';

  // Build breadcrumb items for JSON-LD
  const breadcrumbItems = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Главная',
      item: baseUrl
    }
  ];

  pathnames.forEach((value, index) => {
    const to = `/${pathnames.slice(0, index + 1).join('/')}`;
    const name = customBreadcrumbs[to] || breadcrumbNames[value] || value;
    
    // Special handling for /product/:id
    const linkTo = value === 'product' && index < pathnames.length - 1 ? '/catalog' : to;
    const displayName = value === 'product' && index < pathnames.length - 1 ? 'Каталог' : name;
    
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: index + 2,
      name: displayName,
      item: `${baseUrl}${linkTo}`
    });
  });

  // JSON-LD schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': breadcrumbItems
  };

  return (
    <>
      {/* JSON-LD structured data */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Visual breadcrumbs */}
      <nav 
        className="bg-gray-50 border-b border-gray-200 py-3" 
        aria-label="Breadcrumb"
      >
        <div className="container max-w-7xl mx-auto px-4">
          <ol 
            className="flex flex-wrap items-center gap-2 text-sm" 
            itemScope 
            itemType="https://schema.org/BreadcrumbList"
          >
            {/* Home */}
            <li 
              itemProp="itemListElement" 
              itemScope 
              itemType="https://schema.org/ListItem"
              className="flex items-center"
            >
              <Link 
                to="/" 
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors group"
                itemProp="item"
              >
                <Home size={16} strokeWidth={1.5} className="group-hover:scale-110 transition-transform" />
                <span className="ml-1" itemProp="name">Главная</span>
              </Link>
              <meta itemProp="position" content="1" />
            </li>

            {/* Path segments */}
            {pathnames.map((value, index) => {
              const to = `/${pathnames.slice(0, index + 1).join('/')}`;
              const isLast = index === pathnames.length - 1;
              const name = customBreadcrumbs[to] || breadcrumbNames[value] || value;
              
              // Special handling for /product/:id
              const linkTo = value === 'product' && !isLast ? '/catalog' : to;
              const displayName = value === 'product' && !isLast ? 'Каталог' : name;

              return (
                <li
                  key={to}
                  className="flex items-center gap-2"
                  itemProp="itemListElement"
                  itemScope
                  itemType="https://schema.org/ListItem"
                >
                  <ChevronRight 
                    size={16} 
                    className="text-gray-400 flex-shrink-0" 
                    strokeWidth={1.5} 
                  />
                  {isLast ? (
                    <span 
                      className="text-gray-900 font-medium truncate" 
                      itemProp="name"
                      title={displayName}
                    >
                      {displayName}
                    </span>
                  ) : (
                    <Link
                      to={linkTo}
                      className="text-gray-600 hover:text-gray-900 transition-colors hover:underline truncate"
                      itemProp="item"
                      title={displayName}
                    >
                      <span itemProp="name">{displayName}</span>
                    </Link>
                  )}
                  <meta itemProp="position" content={index + 2} />
                </li>
              );
            })}
          </ol>
        </div>
      </nav>
    </>
  );
};

export default Breadcrumbs;
